// UTF-16 safety for text that crosses a storage or wire boundary.
//
// A non-BMP character — an emoji, a rare CJK glyph, a mathematical letter —
// occupies TWO UTF-16 code units, while every string index in JavaScript counts
// single units. So any cut, search or splice addressed by an index can land
// between the two halves of one character and strand a half. A lone surrogate
// is not encodable as JSON by PostgreSQL, which is why the damage surfaces far
// from where it was made: a write that fails, or mojibake in a render nobody
// can trace back.
//
// Two shapes of exposure, one home:
//   - an INDEX supplied or arithmetic-derived by a model (a `charOffset`
//     continuation handle) — snap it to a character boundary before slicing;
//   - a SUBSTRING supplied by a model (a patch anchor, a window bound) — refuse
//     it outright when it carries half a pair, because the character it was
//     meant to be cannot be recovered from half of it.
//
// A surrogate pair is the SMALLEST indivisible unit here, not the largest. One
// rendered glyph can span many code points — an emoji with a skin tone, a ZWJ
// family, a two-letter flag — and cutting between them yields bare modifiers
// and stray letters rather than a lone surrogate. Cuts that a READER will see
// therefore snap to a grapheme-cluster boundary, not merely a code-point one.

interface SurrogateBoundaryInput {
    text:     string;
    boundary: number;
}

interface CodePointBoundaryInput {
    text:  string;
    index: number;
}

interface CodePointSafeBoundsInput {
    text:  string;
    start: number;
    end:   number;
}

interface CodePointSafeBounds {
    start: number;
    end:   number;
}

interface GraphemeBoundaryInput {
    text:  string;
    index: number;
}

const HIGH_SURROGATE_MIN = 0xD800;
const HIGH_SURROGATE_MAX = 0xDBFF;
const LOW_SURROGATE_MIN  = 0xDC00;
const LOW_SURROGATE_MAX  = 0xDFFF;

function isHighSurrogate(codeUnit: number): boolean {
    return codeUnit >= HIGH_SURROGATE_MIN && codeUnit <= HIGH_SURROGATE_MAX;
}

function isLowSurrogate(codeUnit: number): boolean {
    return codeUnit >= LOW_SURROGATE_MIN && codeUnit <= LOW_SURROGATE_MAX;
}

// True when `boundary` falls between the two halves of one character.
export function splitsSurrogatePair(input: SurrogateBoundaryInput): boolean {
    if (input.boundary <= 0 || input.boundary >= input.text.length) return false;
    return isHighSurrogate(input.text.charCodeAt(input.boundary - 1))
        && isLowSurrogate(input.text.charCodeAt(input.boundary));
}

// The nearest opening boundary at or after `index` that keeps characters whole.
export function codePointSafeStart(input: CodePointBoundaryInput): number {
    return splitsSurrogatePair({text: input.text, boundary: input.index})
        ? input.index + 1
        : input.index;
}

// The nearest closing boundary at or before `index` that keeps characters whole.
export function codePointSafeEnd(input: CodePointBoundaryInput): number {
    return splitsSurrogatePair({text: input.text, boundary: input.index})
        ? input.index - 1
        : input.index;
}

// Both bounds of a slice, each moved INWARD off a split so the cut stays within
// the range asked for rather than widening past it.
export function codePointSafeBounds(input: CodePointSafeBoundsInput): CodePointSafeBounds {
    return {
        start: codePointSafeStart({text: input.text, index: input.start}),
        end:   codePointSafeEnd({text: input.text, index: input.end}),
    };
}

// True when `text` carries half of a surrogate pair with nothing to pair it
// with. A model produces one by reserializing an emoji through a truncated
// `\uD83D` escape — the same drift class a canonical-match fallback exists to
// absorb, except this one is not healable.
export function containsLoneSurrogate(text: string): boolean {
    for (let index = 0; index < text.length; index += 1) {
        const codeUnit = text.charCodeAt(index);
        if (isHighSurrogate(codeUnit)) {
            if (!isLowSurrogate(text.charCodeAt(index + 1))) return true;
            index += 1;
            continue;
        }
        if (isLowSurrogate(codeUnit)) return true;
    }
    return false;
}

// --- grapheme clusters ------------------------------------------------------

const ZERO_WIDTH_JOINER = 0x200d;

// Code points that CONTINUE the cluster they follow rather than starting a new
// one: combining marks, variation selectors, the combining keycap, emoji
// skin-tone modifiers, and the tag characters that spell out subdivision flags.
const CLUSTER_EXTENDER_RANGES: ReadonlyArray<readonly [number, number]> = [
    [0x0300, 0x036f],
    [0x1ab0, 0x1aff],
    [0x1dc0, 0x1dff],
    [0x20d0, 0x20ff],
    [0xfe00, 0xfe0f],
    [0xfe20, 0xfe2f],
    [0x1f3fb, 0x1f3ff],
    [0xe0020, 0xe007f],
];

// Two regional indicators bind into one flag; a cut between them shows both
// letters as bare glyphs.
const REGIONAL_INDICATOR_FIRST = 0x1f1e6;
const REGIONAL_INDICATOR_LAST  = 0x1f1ff;

function isClusterExtender(codePoint: number): boolean {
    return CLUSTER_EXTENDER_RANGES.some(
        (range) => codePoint >= range[0] && codePoint <= range[1],
    );
}

function isRegionalIndicator(codePoint: number | undefined): boolean {
    return codePoint != null
        && codePoint >= REGIONAL_INDICATOR_FIRST
        && codePoint <= REGIONAL_INDICATOR_LAST;
}

function codePointLength(text: string, index: number): number {
    const codePoint = text.codePointAt(index);
    return codePoint != null && codePoint > 0xffff ? 2 : 1;
}

// End offset of the grapheme cluster starting at `start`.
export function graphemeClusterEnd(input: GraphemeBoundaryInput): number {
    const {text, index: start} = input;
    let index = start + codePointLength(text, start);

    if (isRegionalIndicator(text.codePointAt(start))) {
        if (index < text.length && isRegionalIndicator(text.codePointAt(index))) {
            index += codePointLength(text, index);
        }
        return index;
    }

    while (index < text.length) {
        const codePoint = text.codePointAt(index);
        if (codePoint == null) break;
        if (isClusterExtender(codePoint)) {
            index += codePointLength(text, index);
            continue;
        }
        if (codePoint === ZERO_WIDTH_JOINER) {
            const joined = index + 1;
            if (joined >= text.length) break;
            index = joined + codePointLength(text, joined);
            continue;
        }
        break;
    }

    return index;
}

// The nearest closing boundary at or before `index` that keeps whole glyphs.
export function graphemeSafeEnd(input: GraphemeBoundaryInput): number {
    const {text, index: cut} = input;
    if (cut <= 0) return 0;
    if (cut >= text.length) return text.length;

    let index = 0;
    let previousBoundary = 0;
    while (index < cut) {
        previousBoundary = index;
        index = graphemeClusterEnd({text, index});
    }

    return index <= cut ? index : previousBoundary;
}

// The nearest opening boundary at or after `index` that keeps whole glyphs —
// the left-truncation mirror, for a window that keeps a string's TAIL.
export function graphemeSafeStart(input: GraphemeBoundaryInput): number {
    const {text, index: cut} = input;
    if (cut <= 0) return 0;
    if (cut >= text.length) return text.length;

    let index = 0;
    while (index < cut) {
        const next = graphemeClusterEnd({text, index});
        if (next >= cut) return next;
        index = next;
    }

    return index;
}
